// Browser reimplementation of src-tauri/src/crypto.rs.
//
// Wire-format parity is intentional and load-bearing: the Rails server stores
// opaque ciphertext produced by this scheme, and the join/approve handshake
// exchanges X25519-wrapped group keys between devices. The byte layout here
// must match the Rust original exactly:
//
//   AES-256-GCM:  output = nonce(12) || ciphertext || tag(16)   (WebCrypto appends the tag)
//   base64:       standard alphabet, padded (matches Rust base64 STANDARD)
//   X25519:       raw 32-byte scalar-mult output used directly as the AES-256 key
//
// AES-GCM uses Web Crypto (SubtleCrypto). X25519 uses @noble/curves because
// browser Web Crypto support for X25519 is still inconsistent across engines.

import { x25519 } from "@noble/curves/ed25519.js";
import { fromUint8Array, toUint8Array } from "js-base64";

const NONCE_LEN = 12; // AES-GCM nonce
const TAG_LEN = 16; // AES-GCM auth tag
const MIN_CIPHERTEXT_LEN = NONCE_LEN + TAG_LEN; // 28: matches crypto.rs

// --- AES-256-GCM (raw bytes) ---

async function importAesKey(key: Uint8Array): Promise<CryptoKey> {
  if (key.length !== 32) throw new Error("AES key must be 32 bytes");
  return crypto.subtle.importKey("raw", key, "AES-GCM", false, ["encrypt", "decrypt"]);
}

/** Encrypt; returns nonce(12) || ciphertext || tag(16). Mirrors crypto.rs `encrypt`. */
export async function encrypt(key: Uint8Array, plaintext: Uint8Array): Promise<Uint8Array> {
  const nonce = crypto.getRandomValues(new Uint8Array(NONCE_LEN));
  const cryptoKey = await importAesKey(key);
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv: nonce }, cryptoKey, plaintext),
  );
  const out = new Uint8Array(NONCE_LEN + ciphertext.length);
  out.set(nonce, 0);
  out.set(ciphertext, NONCE_LEN);
  return out;
}

/** Decrypt nonce(12) || ciphertext || tag(16). Mirrors crypto.rs `decrypt`. */
export async function decrypt(key: Uint8Array, data: Uint8Array): Promise<Uint8Array> {
  if (data.length < MIN_CIPHERTEXT_LEN) throw new Error("ciphertext too short");
  const nonce = data.subarray(0, NONCE_LEN);
  const ciphertext = data.subarray(NONCE_LEN);
  const cryptoKey = await importAesKey(key);
  return new Uint8Array(
    await crypto.subtle.decrypt({ name: "AES-GCM", iv: nonce }, cryptoKey, ciphertext),
  );
}

// --- Base64 helpers (standard, padded) ---

export const b64Encode = (data: Uint8Array): string => fromUint8Array(data);
export const b64Decode = (s: string): Uint8Array => toUint8Array(s);

// --- Key generation ---

export function generateGroupKey(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(32));
}

export function generateDeviceKeypair(): { privateKey: Uint8Array; publicKey: Uint8Array } {
  const privateKey = x25519.utils.randomSecretKey();
  const publicKey = x25519.getPublicKey(privateKey);
  return { privateKey, publicKey };
}

// --- X25519 key exchange + group-key wrapping ---

/** Raw X25519 shared secret (32 bytes). Mirrors crypto.rs `derive_shared_secret`. */
export function deriveSharedSecret(myPrivate: Uint8Array, theirPublic: Uint8Array): Uint8Array {
  return x25519.getSharedSecret(myPrivate, theirPublic);
}

/** AES-GCM-encrypt the group key with the shared secret, base64. Mirrors `wrap_group_key`. */
export async function wrapGroupKey(groupKey: Uint8Array, sharedSecret: Uint8Array): Promise<string> {
  return b64Encode(await encrypt(sharedSecret, groupKey));
}

/** Reverse of wrapGroupKey; returns the 32-byte group key. Mirrors `unwrap_group_key`. */
export async function unwrapGroupKey(wrappedB64: string, sharedSecret: Uint8Array): Promise<Uint8Array> {
  const decrypted = await decrypt(sharedSecret, b64Decode(wrappedB64));
  if (decrypted.length !== 32) throw new Error("Unwrapped group key wrong length");
  return decrypted;
}

// --- Update/message pipeline (base64 in, base64 out) ---
// Mirrors crypto.rs `encrypt_update` / `decrypt_update`: the plaintext crosses
// as base64 (e.g. a Yjs update or a UTF-8 chat message), so the bridge layer can
// stay byte-identical to the old Tauri command signatures.

export async function encryptUpdate(groupKey: Uint8Array, plaintextB64: string): Promise<string> {
  return b64Encode(await encrypt(groupKey, b64Decode(plaintextB64)));
}

export async function decryptUpdate(groupKey: Uint8Array, encryptedB64: string): Promise<string> {
  return b64Encode(await decrypt(groupKey, b64Decode(encryptedB64)));
}
